import React, { useCallback, useEffect, useState } from 'react';
import { X, Save, ChevronRight, CreditCard, ArrowLeft } from 'lucide-react';
import { loadStripe, Stripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import toast from 'react-hot-toast';
import { GiftCard, GiftCardFormData } from '../types';
import apiService from '../services/api';

interface GiftCardModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave?: (giftCardData: GiftCardFormData) => Promise<void>;
  onIssued?: () => void;
  giftCard?: GiftCard;
  businessId: string;
}

/** Amount input stored as cents digits (e.g. "1234" => $12.34). */
const amountToCentsDigits = (amount: number): string => {
  const n = Number.isFinite(amount) ? amount : 0;
  const cents = Math.round(Math.max(0, n) * 100);
  return cents > 0 ? String(cents) : '';
};

const centsDigitsToAmount = (digits: string): number => {
  const only = String(digits || '').replace(/\D/g, '');
  const cents = only ? parseInt(only, 10) : 0;
  return Number.isFinite(cents) ? cents / 100 : 0;
};

const centsDigitsToMoneyString = (digits: string): string => {
  const only = String(digits || '').replace(/\D/g, '');
  if (!only) return '';
  const cents = parseInt(only, 10);
  return (Number.isFinite(cents) ? cents / 100 : 0).toFixed(2);
};

function GiftCardPaymentForm(props: {
  businessId: string;
  amountLabel: string;
  onBack: () => void;
  onPaid: () => void;
}) {
  const { businessId, amountLabel, onBack, onPaid } = props;
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setError(null);
      if (!stripe || !elements) return;

      try {
        setSubmitting(true);
        const result = await stripe.confirmPayment({
          elements,
          confirmParams: { return_url: window.location.href },
          redirect: 'if_required',
        });

        if (result.error) {
          setError(result.error.message || 'Payment failed. Please try again.');
          return;
        }

        const pi = result.paymentIntent;
        if (!pi?.id) {
          setError('Payment could not be confirmed. Please try again.');
          return;
        }

        const sync = await apiService.syncGiftCardIssuancePaymentIntent(businessId, pi.id);
        if (sync.mappedStatus !== 'SUCCEEDED' || !sync.giftCardId) {
          setError('Payment is still processing. Please wait a moment and refresh your gift cards list.');
          return;
        }

        toast.success('Gift card issued successfully');
        onPaid();
      } catch (err: any) {
        setError(err?.response?.data?.error || err?.message || 'Payment failed. Please try again.');
      } finally {
        setSubmitting(false);
      }
    },
    [businessId, elements, onPaid, stripe]
  );

  return (
    <div className="space-y-4">
      <div className="p-3 border border-gray-200 rounded-lg bg-gray-50 text-sm text-gray-700">
        <div className="text-xs text-gray-500 mb-1">AMOUNT TO CHARGE</div>
        <div className="text-xl font-bold text-gray-900">${amountLabel}</div>
        <p className="text-xs text-gray-500 mt-2 mb-0">
          The gift card is created and emailed to the recipient after payment succeeds.
        </p>
      </div>
      <form onSubmit={handleSubmit} className="space-y-3">
        <div className="bg-white border border-gray-200 rounded-lg p-3">
          <PaymentElement />
        </div>
        {error ? <p className="text-sm text-red-600">{error}</p> : null}
        <button
          type="submit"
          className="aacore-btn2-primary w-full justify-center gap-2"
          disabled={!stripe || !elements || submitting}
        >
          <CreditCard className="w-4 h-4" />
          {submitting ? 'Processing…' : 'Pay and issue gift card'}
        </button>
      </form>
      <button type="button" onClick={onBack} className="aacore-btn2-outline w-full justify-center gap-2" disabled={submitting}>
        <ArrowLeft className="w-4 h-4" />
        Back to details
      </button>
    </div>
  );
}

const GiftCardModal: React.FC<GiftCardModalProps> = ({
  isOpen,
  onClose,
  onSave,
  onIssued,
  giftCard,
  businessId,
}) => {
  const isEdit = !!giftCard;
  const [step, setStep] = useState<'details' | 'payment'>('details');
  const [formData, setFormData] = useState<GiftCardFormData>({
    recipientEmail: giftCard?.recipientEmail || '',
    recipientName: giftCard?.recipientName || '',
    amount: giftCard?.amount || 0,
    message: '',
    expiresAt: '',
  });
  const [amountCentsDigits, setAmountCentsDigits] = useState(
    giftCard?.amount != null && giftCard.amount > 0 ? amountToCentsDigits(giftCard.amount) : ''
  );

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [stripePromise, setStripePromise] = useState<Promise<Stripe | null> | null>(null);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [paymentError, setPaymentError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setStep('details');
    setClientSecret(null);
    setPaymentError(null);
    setFormData({
      recipientEmail: giftCard?.recipientEmail || '',
      recipientName: giftCard?.recipientName || '',
      amount: giftCard?.amount || 0,
      message: giftCard?.message || '',
      expiresAt: giftCard?.expiresAt || '',
    });
    setAmountCentsDigits(
      giftCard?.amount != null && giftCard.amount > 0 ? amountToCentsDigits(giftCard.amount) : ''
    );
    setErrors({});
  }, [isOpen, giftCard]);

  useEffect(() => {
    if (!isOpen || isEdit) return;
    (async () => {
      try {
        const cfg = await apiService.getStripeConfig();
        setStripePromise(loadStripe(cfg.publishableKey));
      } catch (e: any) {
        console.error('Failed to load Stripe config', e);
        setPaymentError(e?.response?.data?.error || e?.message || 'Stripe is not configured');
      }
    })();
  }, [isOpen, isEdit]);

  const handleInputChange = (field: keyof GiftCardFormData, value: string | number) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: '',
      }));
    }
  };

  const buildPayload = (): GiftCardFormData => ({
    ...formData,
    amount: centsDigitsToAmount(amountCentsDigits),
  });

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.recipientName.trim()) {
      newErrors.recipientName = 'Recipient name is required';
    }

    const amount = centsDigitsToAmount(amountCentsDigits);
    if (!amountCentsDigits || amount <= 0) {
      newErrors.amount = 'Amount must be greater than 0';
    }

    if (!formData.recipientEmail?.trim()) {
      newErrors.recipientEmail = 'Recipient email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.recipientEmail)) {
      newErrors.recipientEmail = 'Please enter a valid email address';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const startPayment = async () => {
    if (!businessId) return;
    setIsSubmitting(true);
    setPaymentError(null);
    try {
      const pi = await apiService.createGiftCardIssuancePaymentIntent(businessId, buildPayload());
      setClientSecret(pi.clientSecret);
      setStep('payment');
    } catch (err: any) {
      const msg = err?.response?.data?.error || err?.message || 'Unable to start payment';
      toast.error(msg);
      setPaymentError(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    if (isEdit) {
      if (!onSave) return;
      setIsSubmitting(true);
      try {
        await onSave(buildPayload());
        onClose();
      } catch (error) {
        console.error('Error saving gift card:', error);
        toast.error('Failed to update gift card');
      } finally {
        setIsSubmitting(false);
      }
      return;
    }

    await startPayment();
  };

  const handlePaid = () => {
    onIssued?.();
    onClose();
  };

  if (!isOpen) return null;

  const amountLabel = centsDigitsToMoneyString(amountCentsDigits) || '0.00';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-gray-900">
              {isEdit ? 'Edit Gift Card' : step === 'payment' ? 'Pay for gift card' : 'Issue New Gift Card'}
            </h2>
            <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
              <X className="w-6 h-6" />
            </button>
          </div>

          {step === 'payment' && clientSecret && stripePromise ? (
            <Elements stripe={stripePromise} options={{ clientSecret, appearance: { theme: 'stripe' } }}>
              <GiftCardPaymentForm
                businessId={businessId}
                amountLabel={amountLabel}
                onBack={() => {
                  setStep('details');
                  setClientSecret(null);
                }}
                onPaid={handlePaid}
              />
            </Elements>
          ) : step === 'payment' ? (
            <div className="text-sm text-red-600 py-4">
              {paymentError || 'Unable to load payment form. Please try again.'}
              <button type="button" onClick={() => setStep('details')} className="mt-3 aacore-btn2-outline w-full">
                Back to details
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Recipient Name *</label>
                <input
                  type="text"
                  value={formData.recipientName}
                  onChange={(e) => handleInputChange('recipientName', e.target.value)}
                  disabled={isEdit}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    errors.recipientName ? 'border-red-500' : 'border-gray-300'
                  } ${isEdit ? 'bg-gray-50' : ''}`}
                  placeholder="Enter recipient name"
                />
                {errors.recipientName && <p className="text-red-500 text-sm mt-1">{errors.recipientName}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Recipient Email *</label>
                <input
                  type="email"
                  value={formData.recipientEmail}
                  onChange={(e) => handleInputChange('recipientEmail', e.target.value)}
                  disabled={isEdit}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    errors.recipientEmail ? 'border-red-500' : 'border-gray-300'
                  } ${isEdit ? 'bg-gray-50' : ''}`}
                  placeholder="Enter recipient email"
                />
                {errors.recipientEmail && <p className="text-red-500 text-sm mt-1">{errors.recipientEmail}</p>}
                {!isEdit && (
                  <p className="text-gray-500 text-xs mt-1">The gift card will be sent via email after payment</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Amount *</label>
                <div className="relative">
                  <span className="absolute left-3 top-2 text-gray-500">$</span>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={centsDigitsToMoneyString(amountCentsDigits)}
                    disabled={isEdit}
                    onChange={(e) => {
                      const digits = e.target.value.replace(/\D/g, '');
                      setAmountCentsDigits(digits);
                      handleInputChange('amount', centsDigitsToAmount(digits));
                      if (errors.amount) {
                        setErrors((prev) => ({ ...prev, amount: '' }));
                      }
                    }}
                    className={`w-full pl-8 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                      errors.amount ? 'border-red-500' : 'border-gray-300'
                    } ${isEdit ? 'bg-gray-50' : ''}`}
                    placeholder="0.00"
                  />
                </div>
                {errors.amount && <p className="text-red-500 text-sm mt-1">{errors.amount}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Message (Optional)</label>
                <textarea
                  value={formData.message}
                  onChange={(e) => handleInputChange('message', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  rows={3}
                  placeholder="Add a personal message..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Expiration Date (Optional)</label>
                <input
                  type="date"
                  value={formData.expiresAt}
                  onChange={(e) => handleInputChange('expiresAt', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                <p className="text-gray-500 text-xs mt-1">Leave blank for no expiration</p>
              </div>

              {!isEdit && paymentError && <p className="text-sm text-red-600">{paymentError}</p>}

              <div className="flex justify-end space-x-3 pt-4">
                <button type="button" onClick={onClose} className="aacore-btn2-outline bg-gray-100 hover:bg-gray-200">
                  <X className="w-4 h-4" />
                  <span>Cancel</span>
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="aacore-btn2-primary disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      <span>{isEdit ? 'Saving...' : 'Continue to payment...'}</span>
                    </>
                  ) : isEdit ? (
                    <>
                      <Save className="w-4 h-4" />
                      <span>Update Gift Card</span>
                    </>
                  ) : (
                    <>
                      <span>Continue to payment</span>
                      <ChevronRight className="w-4 h-4 shrink-0" />
                    </>
                  )}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default GiftCardModal;
